import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Close as CloseIcon, CameraAlt as CameraIcon } from "@mui/icons-material";

// BarcodeDetector is not yet in the standard TS lib — declare it inline.
interface BarcodeScanResult {
    rawValue: string;
}
interface BarcodeDetectorInstance {
    detect(
        source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement | ImageBitmap,
    ): Promise<BarcodeScanResult[]>;
}
declare global {
    interface Window {
        BarcodeDetector?: new (options?: { formats?: string[] }) => BarcodeDetectorInstance;
    }
}

interface BarcodeScannerDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the decoded barcode string as soon as a scan succeeds. */
    onScan: (barcode: string) => void;
    title?: string;
}

const SUPPORTED = typeof window !== "undefined" && "BarcodeDetector" in window;

export const BarcodeScannerDialog: React.FC<BarcodeScannerDialogProps> = ({
    open,
    onClose,
    onScan,
    title = "Scan Barcode",
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const rafRef = useRef<number>(0);
    const detectorRef = useRef<BarcodeDetectorInstance | null>(null);

    const [error, setError] = useState<string>("");
    const [starting, setStarting] = useState(true);

    const stopCamera = useCallback(() => {
        cancelAnimationFrame(rafRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    const handleClose = useCallback(() => {
        stopCamera();
        setError("");
        setStarting(true);
        onClose();
    }, [stopCamera, onClose]);

    useEffect(() => {
        if (!open || !SUPPORTED) return;

        let cancelled = false;

        const start = async () => {
            setError("");
            setStarting(true);

            try {
                // Prefer rear camera on mobile
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: "environment" } },
                });

                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }

                streamRef.current = stream;
                const video = videoRef.current!;
                video.srcObject = stream;
                await video.play();

                if (cancelled) {
                    stopCamera();
                    return;
                }

                detectorRef.current = new window.BarcodeDetector!({
                    formats: [
                        "ean_13", "ean_8", "upc_a", "upc_e",
                        "code_128", "code_39", "code_93",
                        "qr_code", "data_matrix", "itf",
                    ],
                });

                setStarting(false);

                const scan = async () => {
                    if (cancelled || !detectorRef.current) return;
                    try {
                        const results = await detectorRef.current.detect(video);
                        if (results.length > 0) {
                            stopCamera();
                            onScan(results[0].rawValue);
                            onClose();
                            return;
                        }
                    } catch {
                        // frame not ready yet — keep scanning
                    }
                    rafRef.current = requestAnimationFrame(scan);
                };

                rafRef.current = requestAnimationFrame(scan);
            } catch (err: any) {
                if (!cancelled) {
                    if (err?.name === "NotAllowedError") {
                        setError(
                            "Camera permission denied. Please allow camera access in your browser settings and try again.",
                        );
                    } else if (err?.name === "NotFoundError") {
                        setError("No camera found on this device.");
                    } else {
                        setError(err?.message || "Could not start the camera.");
                    }
                    setStarting(false);
                }
            }
        };

        start();

        return () => {
            cancelled = true;
            stopCamera();
        };
    }, [open, onScan, onClose, stopCamera]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CameraIcon fontSize="small" color="primary" />
                        <Typography variant="h6">{title}</Typography>
                    </Box>
                    <Tooltip title="Close">
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <DialogContent>
                {!SUPPORTED ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Camera barcode scanning is not supported in this browser.
                        Please type the barcode manually, or open this page in
                        Chrome on Android or Safari on iOS 17+.
                    </Alert>
                ) : error ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {error}
                    </Alert>
                ) : (
                    <Box sx={{ position: "relative", width: "100%", mt: 1 }}>
                        {/* Loading overlay */}
                        {starting && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "black",
                                    borderRadius: 1,
                                    minHeight: 240,
                                    zIndex: 1,
                                    gap: 1.5,
                                }}
                            >
                                <CircularProgress size={32} sx={{ color: "white" }} />
                                <Typography variant="body2" sx={{ color: "grey.400" }}>
                                    Starting camera…
                                </Typography>
                            </Box>
                        )}

                        {/* Camera viewfinder */}
                        <Box
                            sx={{
                                position: "relative",
                                display: "inline-block",
                                width: "100%",
                            }}
                        >
                            <video
                                ref={videoRef}
                                autoPlay
                                muted
                                playsInline
                                style={{
                                    width: "100%",
                                    borderRadius: 8,
                                    display: "block",
                                    background: "#000",
                                    minHeight: 240,
                                }}
                            />
                            {/* Aim guide */}
                            {!starting && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: "70%",
                                        height: 80,
                                        border: "2px solid",
                                        borderColor: "primary.main",
                                        borderRadius: 1,
                                        pointerEvents: "none",
                                        boxShadow:
                                            "0 0 0 9999px rgba(0,0,0,0.35)",
                                    }}
                                />
                            )}
                        </Box>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            textAlign="center"
                            sx={{ mt: 1 }}
                        >
                            Point the camera at a barcode — it scans automatically
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};
